import React, { useCallback, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, Pill } from "../lib/ui";

const AUTHORITIES = ["Canonical", "Approved", "Reference", "External", "Unverified", "Historical"];

export default function RetrievalExplorer() {
  const [query, setQuery] = useState("movement of everyday people");
  const [authorityMin, setAuthorityMin] = useState("Reference");
  const [limit, setLimit] = useState(8);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const runSearch = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post("/knowledge/search", {
        query, authority_min: authorityMin, limit,
      });
      setResults(res.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, [query, authorityMin, limit]);

  return (
    <Shell title="Retrieval Explorer">
      <div className="bo-eyebrow">Knowledge</div>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 8px" }} data-testid="explorer-title">
        See what Brand OS finds.
      </h1>
      <p style={{ color: "var(--bo-muted)", maxWidth: 720, marginBottom: 24 }}>
        Type a query the way the Copywriter would internally. The retriever returns the top chunks weighted by
        BM25 score plus source authority. Superseded and retired sources are excluded.
      </p>

      <div className="bo-card" data-testid="explorer-form">
        <div className="bo-field" style={{ marginBottom: 14 }}>
          <label className="bo-label" htmlFor="exp-query">Query</label>
          <input
            id="exp-query"
            className="bo-input"
            data-testid="explorer-query-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
          />
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div className="bo-field" style={{ minWidth: 200 }}>
            <label className="bo-label" htmlFor="exp-auth">Authority floor</label>
            <select
              id="exp-auth"
              className="bo-input"
              data-testid="explorer-authority-select"
              value={authorityMin}
              onChange={(e) => setAuthorityMin(e.target.value)}
            >
              {AUTHORITIES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="bo-field" style={{ maxWidth: 100 }}>
            <label className="bo-label" htmlFor="exp-limit">Limit</label>
            <input
              id="exp-limit"
              className="bo-input"
              data-testid="explorer-limit-input"
              type="number" min="1" max="25"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            />
          </div>
          <button
            className="bo-btn bo-btn-primary"
            data-testid="explorer-search-button"
            onClick={runSearch}
            disabled={busy || !query.trim()}
          >
            {busy ? "Searching…" : "Search"}
          </button>
        </div>
      </div>

      <ErrorBanner error={error} testId="explorer-error" />

      {results && (
        <div className="bo-card" data-testid="explorer-results">
          <div className="bo-eyebrow">Results</div>
          <h2 className="bo-card-title" data-testid="explorer-result-count">
            {results.hits.length} hit{results.hits.length === 1 ? "" : "s"} for {JSON.stringify(results.query)}
          </h2>
          {results.hits.length === 0 && (
            <EmptyState title="No hits" description="Try lowering the authority floor or rephrasing." />
          )}
          <div style={{ display: "grid", gap: 12 }}>
            {results.hits.map((h) => (
              <div
                key={h.chunk_id}
                data-testid={`hit-${h.authority}`}
                style={{
                  padding: "14px 16px",
                  border: "1px solid var(--bo-line)",
                  borderRadius: "var(--bo-radius-sm)",
                  background: "var(--bo-panel-alt)",
                }}
              >
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                  <Pill>score {h.score}</Pill>
                  <Pill tone={h.authority === "Canonical" || h.authority === "Approved" ? undefined : "muted"}>
                    {h.authority}
                  </Pill>
                  <Pill tone="muted">{h.lifecycle}</Pill>
                </div>
                <div style={{ color: "var(--bo-ink-soft)", lineHeight: 1.55 }}>{h.text}</div>
                <div style={{ marginTop: 8, fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>
                  chunk {h.chunk_id.slice(0, 8)} · source {h.source_id.slice(0, 8)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}
